import { Box, Stack, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import LogoPlaceholder from '../../../../../assets/images/logo-placeholder.jpg';
import { CURRENCY, IS_SA } from '../../../../../config/appConfig';
import { AppRoutes } from '../../../../../constants/appRoutes';
import { MarketplaceStatuses } from '../../../../../constants/marketplaceStatuses';
import { currencyFormatterHelper } from '../../../../../helpers/currencyFormatterHelper';
import { LoanStatuses } from '../../../../../constants/loanStatus';
import { buildLocalizeRoute } from '../../../../../helpers/languageHelper';
import { SummaryCard } from '../../../../../shared/components/common/summaryCard';
import { CustomTooltip } from '../../../../../shared/components/common/tooltip';
import { ButtonWrapper } from '../../../../../shared/components/formUtilities/useButton';
import { CustomProgressBar, PROGRESS_TOOLTIP_SLOT_PROPS } from '../../../../../shared/components/formUtilities/useProgressBar';
import { TextWrapper } from '../../../../../shared/components/formUtilities/useTextWrapper';
import RatingBadge from '../../../../../pages/investor/components/ratingBadge';

export const MarketplaceItems = ({ item, isCollection, status }) => {
	const { t } = useTranslation();

	const isHistory = () => {
		return status === MarketplaceStatuses.Past;
	};

	const getPercentage = (item) => {
		if (!isHistory()) {
			let investPercentage = (item?.investedAmount / item?.campaignAmount) * 100;
			let formattedValue = parseFloat(
				(Math.floor(investPercentage * 100) / 100).toString().substring(0, 4)
			);
			return formattedValue > 100 ? 100 : formattedValue;
		}
		return 100;
	};

	const getButtonLabel = () => {
		if (item?.loanStatus === LoanStatuses.Fundraising) return t('invest');
		if (item?.loanStatus === LoanStatuses.PreFundraising) return t('comingSoon');
		return t('readMore');
	};

	const getDetailURL = () => {
		let path = '';
		path = isCollection
			? `${AppRoutes.Investor}/${AppRoutes.Marketplace}/collection`
			: `${AppRoutes.Investor}/${AppRoutes.Marketplace}/business-details`;
		path = `${path}/${item?.id}?status=${status}`;
		return buildLocalizeRoute(path);
	};

	return (
		<Box>
			<Link
				className={`!text-start cursor-pointer`}
				to={getDetailURL()}>
				<SummaryCard
					classList="!p-0 overflow-hidden"
					children={
						<Box>
							<Box className={`overflow-hidden h-[275px] w-full${isHistory() ? ' blur-sm' : ''}`}>
								<img
									className="object-cover w-full h-full"
									src={item?.logo ? item?.logo : LogoPlaceholder}
									alt="Marketplace Logo"
								/>
							</Box>

							<Stack
								gap={1}
								className="p-4">
								<Stack gap={1}>
									<Box style={{ height: '62px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
										{isHistory() ? (
											<Box className="blur-sm">XXXX XXX XXXX XXXXX</Box>
										) : (
											<TextWrapper
												classList="!font-bold !text-[20px] !text-[#063236] leading-tight"
												text={!IS_SA ? item?.businessName : item?.industry || ''}
											/>
										)}
									</Box>
									<Box className="marketplace-business-name mb-1">
										<Tooltip
											title={!isHistory() && item?.campaignAmount > item?.investedAmount
												? `${t('remaining')}: ${CURRENCY} ${currencyFormatterHelper(item.campaignAmount - item.investedAmount, 0)}`
												: ''
											}
											arrow
											enterTouchDelay={0}
											leaveTouchDelay={3000}
											placement="top"
											slotProps={PROGRESS_TOOLTIP_SLOT_PROPS}
										>
											<Box>
												<CustomProgressBar
													value={getPercentage(item)}
													borderRadius="10px"
													labelColor="#063236"
													labelSize="24px"
													paddingY="24px"
												/>
											</Box>
										</Tooltip>
									</Box>
									<Box className="flex justify-between mt-2 mb-12">
										<Box className="flex flex-col">
											<span className="text-sm" style={{ color: '#000000' }}>{t('funded')}</span>
											<strong style={{ fontSize: '20px' }}>{CURRENCY} {currencyFormatterHelper(isHistory() ? item?.campaignAmount : item?.investedAmount, 0)}</strong>
										</Box>
										<Box className="flex flex-col items-end">
											<span className="text-sm" style={{ color: '#000000' }}>{t('requested')}</span>
											<strong style={{ fontSize: '20px' }}>{CURRENCY} {currencyFormatterHelper(item?.campaignAmount, 0)}</strong>
										</Box>
									</Box>
								</Stack>

								<Box className="flex flex-col">
									{/* Net Yield */}
									<Box className="flex justify-between items-center py-3 border-t border-b border-[#063236]">
										<Box>
											<TextWrapper
												classList="!font-[500] !text-[#063236]"
												type="caption"
												text={
													<span>
														<span>{t('roi')}</span>
														<CustomTooltip
															id="netYieldInfoId"
															text="yieldTooltipInfo"
														/>
													</span>
												}
											/>
										</Box>
										<Box>
											<TextWrapper
												classList="!font-bold !text-[14px] !text-[#063236]"
												text={`${item?.fixed}%`}
											/>
										</Box>
									</Box>

									{/* IRR */}
									<Box className="flex justify-between items-center py-3 border-b border-[#063236]">
										<Box>
											<TextWrapper
												classList="!font-[500] !text-[#063236]"
												type="caption"
												text={
													<span>
														<span>{t('irr')}</span>
														<CustomTooltip
															id="irrInfoId"
															text="IrrTooltipInfo"
														/>
													</span>
												}
											/>
										</Box>
										<Box>
											<TextWrapper
												classList="!font-bold !text-[14px] !text-[#063236]"
												text={`${item?.investorIRR || '25.9'}%`}
											/>
										</Box>
									</Box>

									{/* Tenure */}
									<Box className="flex justify-between items-center py-3 border-b border-[#063236]">
										<Box>
											<TextWrapper
												classList="!font-[500] !text-[#063236]"
												type="caption"
												text={t('tenure')}
											/>
										</Box>
										<Box>
											<TextWrapper
												classList="!font-bold !text-[14px] !text-[#063236]"
												text={`${item?.month} ${t('months')}`}
											/>
										</Box>
									</Box>

									{/* Repayments */}
									<Box className="flex justify-between items-center py-3 border-b border-[#063236]">
										<Box>
											<TextWrapper
												classList="!font-[500] !text-[#063236]"
												type="caption"
												text={t('repayments')}
											/>
										</Box>
										<Box>
											<TextWrapper
												classList="!font-bold !text-[14px] !text-[#063236]"
												text={`${item?.paymentFrequency === 0 ? t('monthly') : t('oneOff')}`}
											/>
										</Box>
									</Box>

									{/* Rating */}
									<Box className="flex justify-between items-center py-3">
										<TextWrapper
											classList="!font-[500] !text-[#063236]"
											type="caption"
											text={t('rating')}
										/>
										<RatingBadge rating={item?.rating} />
									</Box>
								</Box>

								<Box className="flex justify-end mt-4">
									<ButtonWrapper
										classList="!w-[160px] !h-[40px] !bg-[#96ECE0] !text-[#063236] !border-[#96ECE0] !uppercase !rounded-full !font-bold"
										label={getButtonLabel()}
										variant="outlined"
									/>
								</Box>
							</Stack>
						</Box>
					}
				/>
			</Link>
		</Box>
	);
};
